import CheckIcon from '@mui/icons-material/Check';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ImageIcon from '@mui/icons-material/Image';
import {
  Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, List, ListItem, ListItemButton,
  ListItemText, Typography,
} from '@mui/material';
import React from 'react';
import { useAuthProvider, useNotify, useRecordContext } from 'react-admin';
import type { OpenBalenaAuthProvider, OpenBalenaSession } from '../authProvider/openbalenaAuthProvider';
import environment from '../lib/reactAppEnv';

interface FileEntry { name: string; size: number; modified: string; }
type DeviceRecord = { uuid: string; 'api heartbeat state'?: string };

const fmt = (bytes: number) =>
  bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const DeviceSplashLogo: React.FC = () => {
  const record = useRecordContext<DeviceRecord>();
  const authProvider = useAuthProvider<OpenBalenaAuthProvider>();
  const notify = useNotify();

  const [open, setOpen] = React.useState(false);
  const [files, setFiles] = React.useState<FileEntry[]>([]);
  const [loadingFiles, setLoadingFiles] = React.useState(false);
  const [selected, setSelected] = React.useState<string | null>(null);
  const [applying, setApplying] = React.useState(false);

  const isOnline = record?.['api heartbeat state'] === 'online';
  const uuid = typeof record?.uuid === 'string' ? record.uuid : '';
  const remoteBase = environment.REACT_APP_OPEN_BALENA_REMOTE_URL ?? '';
  const uiBase = environment.REACT_APP_OPEN_BALENA_UI_URL ?? '';
  const token = localStorage.getItem('auth') ?? '';

  const openPicker = async () => {
    setOpen(true);
    setSelected(null);
    setLoadingFiles(true);
    try {
      const res = await fetch('/api/files', { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(res.statusText);
      setFiles(await res.json());
    } catch (e) {
      notify(`Bestanden laden mislukt: ${e instanceof Error ? e.message : String(e)}`, { type: 'error' });
    } finally {
      setLoadingFiles(false);
    }
  };

  const applyLogo = async () => {
    if (!selected || !uuid || !remoteBase) return;
    setApplying(true);
    try {
      const session = authProvider?.getSession?.() as OpenBalenaSession | undefined;
      const jwt = session?.jwt ?? '';
      const fileUrl = `${uiBase}/files/${encodeURIComponent(selected)}`;

      const resp = await fetch(`${remoteBase}/splash-logo/${uuid}?jwt=${encodeURIComponent(jwt)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileUrl }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText })) as { error?: string };
        throw new Error(err.error ?? resp.statusText);
      }

      notify('Boot logo ingesteld — wordt actief na herstart', { type: 'success' });
      setOpen(false);
    } catch (e) {
      notify(`Toepassen mislukt: ${e instanceof Error ? e.message : String(e)}`, { type: 'error' });
    } finally {
      setApplying(false);
    }
  };

  if (!record) return null;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <ImageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant='body2' sx={{ fontSize: '13px' }}>Boot splash logo</Typography>
        <Button
          size='small'
          variant='outlined'
          startIcon={<FolderOpenIcon sx={{ fontSize: 14 }} />}
          disabled={!isOnline}
          onClick={() => void openPicker()}
          sx={{ fontSize: '12px', py: 0.25, px: 1 }}
        >
          Kiezen uit File Manager
        </Button>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Kies een boot logo</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loadingFiles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : files.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color='text.secondary'>
                Geen bestanden gevonden in de File Manager.
                Upload eerst een logo via het File Manager menu.
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {files.map((f) => (
                <ListItem
                  key={f.name}
                  disablePadding
                  secondaryAction={selected === f.name ? <CheckIcon color='primary' fontSize='small' /> : undefined}
                >
                  <ListItemButton selected={selected === f.name} onClick={() => setSelected(f.name)}>
                    <ListItemText
                      primary={f.name}
                      secondary={`${fmt(f.size)} · ${new Date(f.modified).toLocaleDateString('nl-NL')}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Annuleren</Button>
          <Button
            variant='contained'
            disabled={!selected || applying}
            startIcon={applying ? <CircularProgress size={14} color='inherit' /> : undefined}
            onClick={() => void applyLogo()}
          >
            {applying ? 'Toepassen...' : 'Toepassen'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeviceSplashLogo;
